<template>
  <div class="cancel-membership-without-login-container">
    <SignUpLayoutBox>
      <OImage v-bind="image" class="hero-image" img-classes="w-full object-cover" />
      <section class="py-7 px-4 md:py-9 md:px-10">
        <h1 class="o-heading-2 md:o-heading-3 mb-rem md:mb-5" v-text="getTranslation('Title')" />
        <p class="ota-body-light" v-text="getTranslation('Description')" />
      </section>
      <section class="bg-gray-100 py-9 px-4 md:py-15 md:px-20 md:flex md:gap-10">
        <div class="mb-10 md:mb-0 flex justify-center">
          <OSvg svg="pause-calender" />
        </div>
        <div>
          <h2 class="o-heading-3 mb-1 md:rem" v-text="getTranslation('DidYouKnow')" />
          <p class="ota-body-light" v-text="getTranslation('DidYouKnowDescription')" />
          <div class="pt-4 md:pt-5 md:text-left">
            <NewButton tag="nuxt-link" to="/account" icon-right="new-chevron-right" color="green" class="w-full md:w-auto">
              {{ $t('CustomerService.GoToAccount') }}
            </NewButton>
          </div>
        </div>
      </section>
      <section class="px-4 py-6 md:p-10">
        <h3 class="ota-body mb-1" v-text="getTranslation('Notice')" />
        <ul style="list-style-type: disc; padding-left: 1.5rem;" class="mb-7">
          <li v-for="(point, idx) in bulletPoints" :key="idx" class="ota-body-light" v-text="point" />
        </ul>
        <p class="ota-body-light" v-text="getTranslation('ExtraInfo')" />
      </section>

      <hr class="mx-4 md:mx-10">

      <client-only>
        <section v-loading="loading" class="px-4 py-6 md:p-10">
          <h4 class="ota-body mb-1" v-text="getTranslation('Instructions')" />
          <form class="grid grid-cols-1 md:grid-cols-2 md:gap-x-5" @submit.prevent="submit">
            <OInput
              id="userId"
              v-model="formData.userId"
              class="mt-5"
              type="text"
              :label="getTranslation('InputFields.UserID')"
              :input-classes="['w-full', 'mb-3', 'o-body']"
              :error="getErrorMessage('userId') || errors.userId"
              :required="true"
              auto-complete="user-id"
              :exclude-symbols="defaultExcludeSymbols"
              @input="onChange($event, 'userId')"
            />
            <hr class="my-5 md:col-span-2">
            <OInput
              id="firstName"
              v-model="formData.firstName"
              type="text"
              :label="$t('SignUp.Form.FirstName')"
              :input-classes="['w-full', 'mb-3', 'o-body']"
              :error="getErrorMessage('firstName') || errors.firstName"
              :required="true"
              auto-complete="given-name"
              :exclude-symbols="defaultExcludeSymbols"
              @input="onChange($event, 'firstName')"
            />
            <OInput
              id="lastName"
              v-model="formData.lastName"
              type="text"
              :label="$t('SignUp.Form.LastName')"
              :input-classes="['w-full', 'mb-3', 'o-body']"
              :error="getErrorMessage('lastName') || errors.lastName"
              :required="true"
              auto-complete="family-name"
              :exclude-symbols="defaultExcludeSymbols"
              @input="onChange($event, 'lastName')"
            />
            <AddressForm
              ref="addressForm"
              class="md:col-span-2"
              hide-submit
              hide-country
              :address="formData"
              :country-code="country"
              :errors="formatErrors(errors)"
              @change="onAddressChange"
            />
            <OInput
              id="email"
              v-model="formData.email"
              type="email"
              :label="$t('SignUp.Form.Email')"
              :input-classes="['w-full', 'mb-3', 'o-body']"
              :error="getErrorMessage('email') || errors.email"
              :required="true"
              auto-complete="email"
              :exclude-symbols="defaultExcludeSymbols"
              @input="onChange($event, 'email')"
            />
            <div class="hidden md:block boxer-image-container relative">
              <OImage v-bind="boxerImage" class="boxer-image" img-classes="w-full object-cover" />
            </div>
            <OSelect
              id="membershipType"
              :value="formData.membershipType"
              class="mb-3"
              :label="getTranslation('InputFields.Membership')"
              :options="membershipTypeOptions"
              :clearable="false"
              :required="true"
              :error="errors.membershipType"
              @input="onChange($event, 'membershipType')"
            />
            <OSelect
              id="cancellationMonth"
              :value="formData.cancellationMonth"
              class="mb-3"
              :label="getTranslation('InputFields.CancelMonth')"
              :options="cancelMonthOptions"
              :clearable="false"
              :required="true"
              :error="errors.cancellationMonth"
              @input="onChange($event, 'cancellationMonth')"
            />
            <div>
              <OInput
                id="reason"
                v-model="formData.reason"
                tag="textarea"
                type="text"
                :label="getTranslation('InputFields.Reason')"
                :input-classes="['w-full', 'mb-3', 'o-body']"
                :error="getErrorMessage('reason') || errors.reason"
                :placeholder="getTranslation('InputFields.ReasonPlaceholder')"
                auto-complete="email"
                @input="onChange($event, 'reason')"
              />
              <div>
                <p class="text-counter o-caption text-right" v-text="getTranslation('InputFields.ReasonCharacterCount')" />
              </div>
            </div>
            <p class="ota-body-light my-10 text-gray-500" v-text="getTranslation('FormNotice')" />
            <div class="button-container md:flex md:gap-4 md:flex-row-reverse md:col-span-2">
              <div class="mb-5 md:flex-1">
                <NewButton type="submit" icon-right="new-chevron-right" color="green" class="w-full">
                  {{ getTranslation('Submit') }}
                </NewButton>
              </div>
              <div class="md:flex-1">
                <NewButton tag="nuxt-link" to="/" icon-right="new-chevron-right" color="black" class="w-full">
                  {{ getTranslation('Cancel') }}
                </NewButton>
              </div>
            </div>
          </form>
        </section>
      </client-only>
    </SignUpLayoutBox>

    <DeleteWithNoLoginSuccess
      ref="deleteWithNoLoginSuccess"
      :title="getTranslation('Modal.Title')"
      :text="getTranslation('Modal.Text')"
      @closed="$router.push('/')"
    />
  </div>
</template>

<script>
import { CountryCodes, DEFAULT_INPUT_EXCLUDE_SYMBOLS } from '@/enums/general'
import { getImagePaths, serializeParameters } from '@/utils/general'
import SignUpLayoutBox from '@/components/SignUpLayoutBox'
import OImage from '@/components/OImage'
import ErrorsMixin from '@/mixins/errors'
import NewButton from '@/components/NewButton'
import OSvg from '@/components/OSvg'
import OInput from '@/components/OInput'
import OSelect from '@/components/OSelect'
import { required, minLength, maxLength, email, numeric } from 'vuelidate/lib/validators'
import { MembershipTypes } from '@/enums/member'
import AddressForm from '@/components/Forms/AddressForm'
import { mapState, mapActions } from 'vuex'
import { DELETE_UNAUTHENTICATED_MEMBERSHIP, SIGNUP_GET_LATEST_DESIGN } from '@/utils/store/actionTypes'
import DeleteWithNoLoginSuccess from '@/components/Modals/DeleteWithNoLoginSuccess'
import { Orientations } from '@/enums/products'

export default {
  name: 'CancelMembership',
  layout: 'homepage',
  components: { SignUpLayoutBox, OImage, NewButton, OSvg, OInput, OSelect, AddressForm, DeleteWithNoLoginSuccess },
  mixins: [ErrorsMixin],
  middleware: ({ route, redirect, store: { state: { app: { locale, country } } } }) => {
    const i18Params = `/${locale}-${country}`
    const queryParamsString = serializeParameters(route.query)

    if (country !== CountryCodes.germany) {
      return redirect(`${i18Params}/?${queryParamsString}`)
    }
  },
  validations () {
    const validations = {
      userId: {
        required,
        numeric
      },
      firstName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(20)
      },
      lastName: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(35)
      },
      email: {
        required,
        email
      },
      reason: {
        maxLength: maxLength(250)
      }
    }

    return { formData: validations }
  },
  async fetch () {
    await this.getAndSetLatestDesign()
  },
  data () {
    return {
      formData: {
        userId: '',
        firstName: '',
        lastName: '',
        street: '',
        houseNumber: '',
        annex: '',
        postalCode: '',
        city: '',
        country: '',
        email: '',
        membershipType: null,
        cancellationMonth: null,
        reason: ''
      },
      errors: {
        userId: '',
        firstName: '',
        lastName: '',
        email: '',
        reason: '',
        membershipType: '',
        cancellationMonth: ''
      },
      latestDesign: {
        [Orientations.menoriginals]: 'gloom'
      },
      loading: false,
      invalidForm: false
    }
  },
  computed: {
    membershipTypeOptions () {
      return Object.values(MembershipTypes).map((value) => {
        return {
          value,
          label: value.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
        }
      })
    },
    cancelMonthOptions () {
      const options = []

      let date = this.$moment.utc().startOf('month')

      const today = new Date().getUTCDate()

      if (today >= 28) {
        date = date.clone().add(1, 'month')
      }

      for (let i = 0; i < 11; i++) {
        options.push({
          value: date.format('YYYY-MM-DD'),
          label: this.$t(`Months[${date.month()}]`)
        })
        date = date.clone().add(1, 'month')
      }
      return options
    },
    image () {
      return getImagePaths({
        s3: true,
        path: '/images/design/twilight/video/twilight-men-originals',
        noMobile: true
      })
    },
    boxerImage () {
      return getImagePaths({
        s3: true,
        path: `/images/design/${this.latestDesign[Orientations.menoriginals]}/products/${this.latestDesign[Orientations.menoriginals]}-men-originals-boxershort`,
        noMobile: true
      })
    },
    defaultExcludeSymbols () {
      return DEFAULT_INPUT_EXCLUDE_SYMBOLS
    },
    cancelVariables () {
      return {
        count: this.formData.reason.length
      }
    },
    bulletPoints () {
      const path = 'CancelWithoutLogin.NoticeBulletPoints'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.getTranslation(`NoticeBulletPoints[${i}]`))
    },
    ...mapState('app', {
      country: state => state.country
    })
  },
  methods: {
    async submit () {
      this.loading = true

      if (!this.formData.membershipType) {
        this.errors.membershipType = this.$t('Form.Error.Required')
        this.loading = false

        return
      }

      if (!this.formData.cancellationMonth) {
        this.errors.cancellationMonth = this.$t('Form.Error.Required')
        this.loading = false

        return
      }

      this.$v.$touch()

      if (this.$v.$invalid) {
        this.invalidForm = true
        this.$otoaster.danger(this.$t(this.getTranslation('FormWarning')))
        this.loading = false

        return
      }

      let userId = this.formData.userId

      try {
        userId = parseInt(userId)
      } catch (error) {}

      try {
        await this.deleteUnauthenticatedMembership({ ...this.formData, membershipType: this.formData.membershipType.value, cancellationMonth: this.formData.cancellationMonth.value, userId, country: this.country.toUpperCase() })
        this.$refs.deleteWithNoLoginSuccess.toggleActivation()
      } catch (error) {
        const errors = this.getPopulatedErrors(error)

        if (errors) {
          this.errors = this.formatErrors(errors)
          this.$otoaster.danger(this.$t(this.getTranslation('FormWarning')))
        } else {
          this.$otoaster.danger(this.$t('Error.general'))
          this.$ota.general.logError(error)
        }
      }

      this.loading = false
    },
    onAddressChange ({ field, address }) {
      this.formData[field] = address[field]
    },
    getTranslation (path = '') {
      return this.$t(`CancelWithoutLogin.${path}`, this.cancelVariables)
    },
    onChange ($event, field) {
      if (field === 'membershipType') {
        this.errors.membershipType = ''
      }

      if (field === 'cancellationMonth') {
        this.errors.cancellationMonth = ''
      }

      this.formData[field] = $event
    },
    getErrorMessage (fieldName) {
      const item = this.$v.formData[fieldName]

      if (!item.$invalid || !this.invalidForm) {
        return ''
      }

      let errorMessage = this.$t('Error.invalid', { label: this.$t(`Form.Address.${fieldName}`) })

      const validations = {
        required: this.$t('Form.Error.Required'),
        email: this.$t('Form.Error.EmailIncorrect'),
        maxLength: this.$t('Form.Error.MaxLength', { value: item.$params.maxLength?.max }),
        minLength: this.$t('Form.Error.MinLength', { amount: item.$params.minLength?.min })
      }

      Object.keys(validations).forEach((rule) => {
        if (typeof item[rule] !== 'undefined' && !item[rule]) {
          errorMessage = validations[rule]
        }
      })

      return errorMessage
    },
    async getAndSetLatestDesign () {
      try {
        const variables = {
          women: false,
          men: true,
          addon: false,
          boys: false
        }

        const designs = await this.getLatestDesign(variables)

        const menOriginalsDesign = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.menoriginals))

        if (menOriginalsDesign) {
          this.latestDesign[Orientations.menoriginals] = menOriginalsDesign.slug
        }
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    ...mapActions('user', {
      deleteUnauthenticatedMembership: DELETE_UNAUTHENTICATED_MEMBERSHIP
    }),
    ...mapActions('signup', {
      getLatestDesign: SIGNUP_GET_LATEST_DESIGN
    })
  }
}
</script>

<style scoped lang="postcss">
.cancel-membership-without-login-container {
  >>> .ogray-backdrop {
    @apply w-full !important;
    max-width: unset !important;
  }

  .oimage.hero-image >>> img {
    @apply w-full object-cover;
    object-position: top;
    max-height: 30rem;
  }

  >>> textarea {
    height: 13rem;
  }

  .text-counter {
    margin-top: -1rem;
  }
}

@screen md {
  .cancel-membership-without-login-container {
    .oimage.hero-image >>> img {
      max-height: 42rem;
    }
  }

  >>> .address-grid {
    grid-template-areas: "street houseNumber" "postalCode annex" "postalCode ." "city ." !important;
  }

  .boxer-image-container {
    grid-column: 2;
    grid-row: 5 / span 4;

    .oimage.boxer-image >>> img {
      @apply absolute top-0 object-cover pointer-events-none;
      top: -9rem;
      right: -22rem;
      height: 62rem;
      width: auto;
      max-width: unset;
    }
  }

  .button-container {
    >>> .new-o-button-body {
      @apply py-3 px-2;
    }
  }
}
</style>
